/**
 * Column sorting for the Chase queue (Tali, 2026-10-14: "order by column
 * name"). Pure, so it is testable without a DOM (the filters.ts split).
 *
 * The default stays what the queue has always done — oldest deadline first
 * — so nobody opens it to a different order than yesterday's.
 */
import { carerKeysOf, shiftDateOf, type DatedRow, type FilterableRow } from "./filters";

export type SortKey = "client" | "date" | "carer" | "age";
export type SortDir = "asc" | "desc";
export interface SortChoice {
  key: SortKey;
  dir: SortDir;
}

export const DEFAULT_SORT: SortChoice = { key: "age", dir: "asc" };

export interface SortableRow extends FilterableRow, DatedRow {
  caseId: string;
  dueEnd: string | null;
  slots: { date: string | null; start?: number | null }[];
}

function text(v: string | null | undefined): string {
  return (v ?? "").toLocaleLowerCase("en-GB");
}

/** Blank sorts last whichever way the column runs: an unnamed client or an
 *  undated shift is a fault to find, not something to sort to the top. */
function compareText(a: string, b: string): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, "en-GB");
}

function value(r: SortableRow, key: SortKey): string {
  switch (key) {
    case "client":
      return text(r.clientName);
    case "carer":
      return text(carerKeysOf(r)[0]?.label);
    case "date": {
      const d = shiftDateOf(r);
      const start = r.slots[0]?.start;
      return d ? `${d} ${String(start ?? 0).padStart(4, "0")}` : "";
    }
    case "age":
      return r.dueEnd ?? "";
  }
}

export function sortRows<R extends SortableRow>(rows: R[], choice: SortChoice): R[] {
  const sign = choice.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = value(a, choice.key);
    const vb = value(b, choice.key);
    if (!va || !vb) return compareText(va, vb); // blanks last, both directions
    return sign * compareText(va, vb) || a.caseId.localeCompare(b.caseId);
  });
}

/** Clicking a header: the same column flips direction, a new one starts
 *  ascending. */
export function nextSort(current: SortChoice, key: SortKey): SortChoice {
  return current.key === key ? { key, dir: current.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" };
}
